import { Box, Typography } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";

import LayerControlItem from "./LayerControlItem";

interface FolderBoundariesControlProps {
  /** Whether folder shapes (boundary layers and extent boxes) are drawn. */
  visible: boolean;
  onToggleVisibility: () => void;
  /** Folders the map has a shape for right now (0 disables the toggle). */
  folderCount: number;
  /** How many of those have a boundary layer of their own. */
  boundaryCount: number;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The listing map's folder layer: the open folder's boundary and each
 * sub-folder's boundary (or, without one, the dashed box around its
 * projects). Its own eye, separate from Project Areas, so folders and
 * projects can be shown or hidden independently.
 */
const FolderBoundariesControl: React.FC<FolderBoundariesControlProps> = ({
  visible,
  onToggleVisibility,
  folderCount,
  boundaryCount,
  expanded,
  onExpandedChange,
}) => {
  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: "#e2e8f0", fontSize: "0.75rem" }}>
        {folderCount === 0
          ? "No folders on the map here."
          : boundaryCount > 0
            ? `${plural(folderCount, "folder")} on the map, ${boundaryCount} with a boundary layer.`
            : `${plural(folderCount, "folder")} on the map, drawn as boxes around their projects.`}
      </Typography>
      <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", lineHeight: 1.4 }}>
        Give a folder its real outline from a folder's menu: Add boundary… takes a file or a property
        boundary data source.
      </Typography>
    </Box>
  );

  return (
    <LayerControlItem
      title="Folders"
      icon={<FolderIcon fontSize="small" />}
      visible={visible}
      onToggleVisibility={onToggleVisibility}
      enabled={folderCount > 0}
      disabledReason="No folders on the map here"
      drawingContent={content}
      drawingContentLabel="Folders"
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
};

export default FolderBoundariesControl;
